import React, { createContext, useContext, useState, useCallback } from 'react';
import api from '@/services/api';
import { toast } from 'sonner';

interface ThemeEditorContextType {
  isEditing: boolean;
  setIsEditing: (value: boolean) => void;
  themeSettings: any;
  setThemeSettings: (settings: any) => void;
  updateSetting: (key: string, value: any) => void;
  saveSettings: () => Promise<void>;
  isLoading: boolean;
}

const ThemeEditorContext = createContext<ThemeEditorContextType | undefined>(undefined);

export const ThemeEditorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [themeSettings, setThemeSettings] = useState<any>({});
  const [isLoading, setIsLoading] = useState(false);

  const updateSetting = useCallback((key: string, value: any) => {
    setThemeSettings((prev: any) => {
      const newSettings = { ...prev };
      // Handle nested keys like 'hero.title'
      const keys = key.split('.');
      let current = newSettings;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newSettings;
    });
  }, []);

  const saveSettings = async () => {
    setIsLoading(true);
    try {
      await api.store.saveThemeSettings(themeSettings);
      toast.success('تم حفظ التعديلات بنجاح');
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('حدث خطأ أثناء حفظ التعديلات');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ThemeEditorContext.Provider
      value={{
        isEditing,
        setIsEditing,
        themeSettings,
        setThemeSettings,
        updateSetting,
        saveSettings,
        isLoading,
      }}
    >
      {children}
    </ThemeEditorContext.Provider>
  );
};

export const useThemeEditor = () => {
  const context = useContext(ThemeEditorContext);
  if (context === undefined) {
    throw new Error('useThemeEditor must be used within a ThemeEditorProvider');
  }
  return context;
};

// Ensure HMR works by separating the hook and provider if necessary, 
// though often just moving the hook to the bottom or ensuring proper naming helps.
// The main issue is usually mixed logic.
